import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Modal, Switch } from 'antd';

import API from 'src/apis';
import { setThreadFocus } from 'src/redux/slices/open_ai';

import SubscriptionCard from 'src/components/molecules/subscription_card';

import closeIcon from 'src/assets/images/close_icon.svg';
import LandingIcon from 'src/assets/images/landing_page.gif';

import styles from './index.module.scss';

import { STATIC_PLANS, SUBSCRIPTION_CONST } from 'src/common/constants';
import { setIsPollingActive, setPollingTimeLeft } from 'src/redux/slices/timer';
import { useAppSelector } from 'src/redux/store';

const SubscriptionPage: React.FunctionComponent = () => {

    const { YEARLY, MONTHLY, TATVA_AI_BASIC, TATVA_AI_PRO_MONTHLY, TATVA_AI_PRO_YEARLY } = SUBSCRIPTION_CONST;

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { isPollingActive, initialPollingTime, userData } = useAppSelector(state => state.timer);

    const [width, setWidth] = useState<number>(window.innerWidth);
    const [planType, setPlanType] = useState<string>(YEARLY);
    const [loading, setLoading] = useState<boolean>(false);
    const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([]);
    const [plans, setPlans] = useState<any[]>([]);

    const resetTimer = () => {
        dispatch(setPollingTimeLeft(initialPollingTime));
        localStorage.setItem('POLLING_TIME_LEFT', initialPollingTime.toString());
        dispatch(setIsPollingActive(false));
    };

    const startTimer = () => {
        resetTimer();
        dispatch(setIsPollingActive(true));
    };

    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        getAllPlans();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const onChange = (checked: boolean) => {
        setPlanType(checked ? YEARLY : MONTHLY);
    };

    const getAllPlans = async () => {
        try {
            setLoading(true);
            const plans = await API.GetAllSubscriptionPlans();
            setPlans(plans);

            const plansMap = plans.reduce((map: any, plan: any) => {
                map[plan.trp_name] = plan.trp_id;
                return map;
            }, {} as Record<string, any>);

            const userDetails = JSON.parse(localStorage.getItem('userDetails') as string);
            const subscribedPlan = userDetails?.subscribed_plan?.length ? userDetails?.subscribed_plan : [TATVA_AI_BASIC];
            let staticPlans = STATIC_PLANS.map((plan: any) => {
                if (subscribedPlan.includes(plan.trp_name)) {
                    plan = {
                        ...plan,
                        subscribed_plan: true,
                    };
                }
                switch (plan.trp_name) {
                    case TATVA_AI_PRO_MONTHLY:
                    case TATVA_AI_PRO_YEARLY:
                        return {
                            ...plan,
                            trp_id: plansMap[plan.trp_name] || '',
                        };
                    default:
                        return plan;
                }
            });

            const isSubscribedPro = staticPlans.some((plan) => plan.subscribed_plan && [TATVA_AI_PRO_MONTHLY, TATVA_AI_PRO_YEARLY].includes(plan.trp_name));
            if (isSubscribedPro) {
                staticPlans = staticPlans.filter((plan) => plan.trp_name !== TATVA_AI_BASIC);
                if (staticPlans.find(plan => plan.subscribed_plan).trp_name === TATVA_AI_PRO_YEARLY) {
                    staticPlans = staticPlans.map((plan) => {
                        if (plan.trp_name === TATVA_AI_PRO_MONTHLY) {
                            return { ...plan, subscribed_pro: true };
                        }
                        return { ...plan };
                    });
                }
            }
            setSubscriptionPlans(staticPlans);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!plans.length || !userData) return;
        const plansMap = plans.reduce((map: any, plan: any) => {
            map[plan.trp_name] = plan.trp_id;
            return map;
        }, {} as Record<string, any>);
        const userDetails = JSON.parse(localStorage.getItem('userDetails') as string);
        const subscribedPlan = userDetails?.subscribed_plan?.length ? userDetails?.subscribed_plan : [TATVA_AI_BASIC];
        let staticPlans = STATIC_PLANS.map((plan: any) => {
            if (subscribedPlan.includes(plan.trp_name)) {
                plan = {
                    ...plan,
                    subscribed_plan: true,
                };
            }
            switch (plan.trp_name) {
                case TATVA_AI_PRO_MONTHLY:
                case TATVA_AI_PRO_YEARLY:
                    return {
                        ...plan,
                        trp_id: plansMap[plan.trp_name] || '',
                    };
                default:
                    return plan;
            }
        });

        const isSubscribedPro = staticPlans.some((plan) => plan.subscribed_plan && [TATVA_AI_PRO_MONTHLY, TATVA_AI_PRO_YEARLY].includes(plan.trp_name));
        if (isSubscribedPro) {
            staticPlans = staticPlans.filter((plan) => plan.trp_name !== TATVA_AI_BASIC);
            if (staticPlans.find(plan => plan.subscribed_plan).trp_name === TATVA_AI_PRO_YEARLY) {
                staticPlans = staticPlans.map((plan) => {
                    if (plan.trp_name === TATVA_AI_PRO_MONTHLY) {
                        return { ...plan, subscribed_pro: true };
                    }
                    return { ...plan };
                });
            }
        }
        setSubscriptionPlans(staticPlans);
    }, [userData]);

    return (
        <div className={styles.subscription_page}>
            {width < 900 &&
                <div className={styles.mobile_subscription_header}>
                    <img className={styles.mobile_logout_icon} src={closeIcon} alt='close_icon' onClick={() => { dispatch(setThreadFocus(true)); navigate('/profile'); }} />
                    <span className={styles.mobile_subscription_heading}>My Subscription</span>
                </div>
            }
            <div className={styles.subscription_wrapper}>
                <div className={styles.subscription_heading}>
                    {width < 900
                        ? <div className={styles.subscription_heading_inner} style={planType === MONTHLY ? { background: 'linear-gradient(45deg, #deccff, #ffffff 80%)' } : {}}>
                            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', justifyContent: 'center', alignItems: 'center' }}>
                                <span className={styles.plan_type}>Monthly</span>
                                <Switch
                                    value={planType === YEARLY}
                                    defaultValue={planType === YEARLY}
                                    onChange={onChange}
                                    style={{ background: 'linear-gradient(125deg, #4747d2, #a524f9 80%)' }}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span className={styles.plan_type}>Yearly</span>
                                    <span className={styles.discount_tag}>33% OFF</span>
                                </div>
                            </div>
                            {isPollingActive && <span className={styles.notice_tag}>We are processing your payment. Please wait for 5 minutes; it will be automatically reflected here within that time. Please do not make another payment during this period. Thank you for your patience.</span>}
                        </div>
                        : <div>
                            <h1 style={{ color: '#454551' }}>
                                Tatva AI Subscription Plans
                            </h1>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', backgroundColor: '#FBF5E8', padding: '15px 30px', borderRadius: '15px', border: '1px solid #E8DCC1' }}>
                                <span className={styles.plan_type} style={{ color: '#454551' }}>Monthly</span>
                                <Switch
                                    value={planType === YEARLY}
                                    defaultValue={planType === YEARLY}
                                    onChange={onChange}
                                    style={{ background: 'linear-gradient(125deg, #4747d2, #a524f9 80%)' }}
                                />
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span className={styles.plan_type} style={{ color: '#454551' }}>Yearly</span>
                                    <span className={styles.discount_tag}>33% OFF</span>
                                </div>
                            </div>
                            {isPollingActive &&
                                <div style={{ width: '500px', marginTop: '10px', backgroundColor: '#FBF5E8', padding: '15px 30px', borderRadius: '15px', border: '1px solid #E8DCC1', color: '#454551', fontSize: '16px' }}>
                                    We are processing your payment. Please wait for few minutes; it will be automatically reflected here within that time. Please do not make another payment during this period. Thank you for your patience.
                                </div>
                            }
                        </div>
                    }
                </div>
                <div className={styles.subscription_box_wrapper} style={loading ? { alignItems: 'center', marginTop: '-10%' } : {}}>
                    {loading
                        ? <img src={LandingIcon} style={{ height: '100px', width: '100px' }} />
                        : <div className={styles.subscription_boxes}
                            style={
                                subscriptionPlans.filter(s => s.trp_period === planType).length <= 2
                                    ? { maxWidth: '800px' } : {}
                            }>
                            {subscriptionPlans.filter(s => s.trp_period === planType).map((subscription) => (
                                <SubscriptionCard subscription={subscription} startTimer={startTimer} isPollingActive={isPollingActive} />
                            ))}
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}

export default SubscriptionPage;